import React, { useState, useCallback } from 'react';
import Grid from '@mui/material/GridLegacy';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import { People, Delete, ContentCopy } from '@mui/icons-material';

function userMongoId(user) {
  const id = user?._id;
  if (id == null) return '';
  if (typeof id === 'string') return id;
  if (typeof id === 'object' && id.$oid) return String(id.$oid);
  if (typeof id?.toString === 'function') return id.toString();
  return String(id);
}

export default function UsersTab({
  users = [],
  onDeleteUser,
}) {
  const [copyToast, setCopyToast] = useState('');

  const copyId = useCallback(async (id) => {
    const s = String(id || '').trim();
    if (!s) return;
    try {
      await navigator.clipboard.writeText(s);
      setCopyToast('User ID copied');
    } catch {
      setCopyToast('Could not copy — select and copy manually');
    }
  }, []);
  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 4 }}>
        User Management
      </Typography>

      {users.length === 0 ? (
        <Card sx={{
          p: 6,
          textAlign: 'center',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <People sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No users yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Users will appear here once they register
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {users.map((user) => (
            <Grid item xs={12} sm={6} md={4} key={user._id}>
              <Card sx={{
                height: '100%',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                transition: 'all 0.3s ease',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{
                      width: 64,
                      height: 64,
                      mr: 2,
                      bgcolor: user.role === 'admin' ? 'primary.main' : 'secondary.main',
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}>
                      {user.name?.charAt(0)?.toUpperCase() || 'U'}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {user.name || 'Unknown User'}
                      </Typography>
                      <Chip
                        label={user.role}
                        size="small"
                        sx={{
                          bgcolor: user.role === 'admin' ? 'rgba(102, 126, 234, 0.1)' : 'rgba(158, 158, 158, 0.1)',
                          color: user.role === 'admin' ? 'primary.main' : 'text.secondary',
                          fontWeight: 500,
                          textTransform: 'capitalize'
                        }}
                      />
                    </Box>
                  </Box>

                  <Box sx={{
                    p: 1.5,
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        User ID (for venue contributor)
                      </Typography>
                      <Tooltip title="Copy ID">
                        <IconButton
                          size="small"
                          aria-label="Copy user id"
                          onClick={() => copyId(userMongoId(user))}
                          sx={{ ml: 'auto' }}
                        >
                          <ContentCopy sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography
                      variant="body2"
                      component="code"
                      sx={{
                        display: 'block',
                        fontFamily: 'ui-monospace, Consolas, monospace',
                        fontSize: '0.8rem',
                        wordBreak: 'break-all',
                        userSelect: 'all',
                      }}
                    >
                      {userMongoId(user) || '—'}
                    </Typography>
                  </Box>

                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    border: '1px solid rgba(102, 126, 234, 0.2)',
                    mb: 2
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Email</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: 'break-word', mb: 1 }}>
                      {user.email}
                    </Typography>
                    {user.phone && (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Phone</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>{user.phone}</Typography>
                      </>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      <Chip
                        label={user.isVerified ? 'Verified' : 'Not Verified'}
                        size="small"
                        color={user.isVerified ? 'success' : 'warning'}
                        sx={{ fontSize: '0.7rem' }}
                      />
                      {user.gender && (
                        <Chip label={user.gender} size="small" sx={{ fontSize: '0.7rem', textTransform: 'capitalize' }} />
                      )}
                      {user.dateOfBirth && (
                        <Chip
                          label={`DOB: ${new Date(user.dateOfBirth).toLocaleDateString()}`}
                          size="small"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      Joined {new Date(user.createdAt).toLocaleDateString()}
                    </Typography>
                    {user.role !== 'admin' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => onDeleteUser(user)}
                        fullWidth
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                      >
                        Delete User
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Snackbar open={Boolean(copyToast)} autoHideDuration={2500} onClose={() => setCopyToast('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={copyToast.includes('Could not') ? 'warning' : 'success'} onClose={() => setCopyToast('')} sx={{ width: '100%' }}>
          {copyToast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
